import type { TrendTone } from '../components/TrendChip/TrendChip';

interface Trend {
  label: string;
  tone: TrendTone;
  arrow?: 'up' | 'down';
}

interface TrendOptions {
  // A rate (0 to 1): the change is in points, not in percent of itself.
  isRate?: boolean;
  // A delay: going down is good.
  lowerIsBetter?: boolean;
  // Neither good nor bad: grey, with the arrow only.
  neutral?: boolean;
}

// The change of a figure against the previous period. null when there is nothing to compare:
// no previous period (all time) or a missing figure. A count at 0 before has no percent: "+3"
// (the whole figure is new), or "=" when it is still 0.
const getTrend = (
  language: string,
  value: number | null | undefined,
  previous: number | null | undefined,
  options: TrendOptions = {},
): Trend | null => {
  if (value === null || value === undefined || previous === null || previous === undefined) {
    return null;
  }
  const { isRate, lowerIsBetter, neutral } = options;
  if (!isRate && !previous) {
    if (!value) {
      return { label: '=', tone: 'neutral' };
    }
    return { label: `+${value.toLocaleString(language)}`, tone: 'up', arrow: 'up' };
  }
  const change = isRate ? (value - previous) * 100 : ((value - previous) / previous) * 100;
  const rounded = Math.round(change * (isRate ? 10 : 1)) / (isRate ? 10 : 1);
  if (!rounded) {
    return { label: '=', tone: 'neutral' };
  }
  const number = Math.abs(rounded).toLocaleString(language, { maximumFractionDigits: 1 });
  const isUp = rounded > 0;
  const label = `${isUp ? '+' : '−'}${number}${isRate ? ' pts' : ' %'}`;
  let tone: TrendTone = isUp !== !!lowerIsBetter ? 'up' : 'down';
  if (neutral) {
    tone = 'neutral';
  }
  return { label, tone, arrow: isUp ? 'up' : 'down' };
};

export type { Trend };

export default getTrend;
